import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, deleteUser } from '../../store/slices/settingsSlice';
import EditUserDialog from './EditUserDialog';
import ChangePasswordDialog from './ChangePasswordDialog';
import AddUserDialog from './AddUserDialog';
import LoadingSpinner from '../common/LoadingSpinner';
import OrgHierarchyView from './OrgHierarchyView';

const UsersTab = ({ isMaximized = true }) => {
  const dispatch = useDispatch();
  const { users = [], pagination = {}, loading = false } = useSelector(state => state.settings || {});
  const { user: currentUser } = useSelector(state => state.auth);
  const [page, setPage] = useState(1);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordChangeUser, setPasswordChangeUser] = useState(null);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortedUsers, setSortedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState('list'); // 'list' | 'hierarchy'

  const userRole = currentUser?.profile?.role;
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin';
  const hasEditPermission = isSuperAdmin || isAdmin;

  console.log('UsersTab State:', {
    users,
    pagination,
    loading,
    userRole,
    isSuperAdmin,
    currentUser
  });

  useEffect(() => {
    dispatch(fetchUsers({ page, limit: 10 }));
  }, [dispatch, page]);

  useEffect(() => {
    // Sort and filter users when users array, sort order, or search query changes
    // Hide crew roles from the settings users list
    const HIDDEN_ROLES = ['crew_leader', 'crew_member'];
    let filteredUsers = users.filter(user => !HIDDEN_ROLES.includes(user.profile?.role));

    // Apply search filter if there's a query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredUsers = filteredUsers.filter(user => 
        user.profile.firstName.toLowerCase().includes(query) || 
        user.profile.lastName.toLowerCase().includes(query) || 
        user.email.toLowerCase().includes(query) || 
        user.profile.role.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    filteredUsers.sort((a, b) => {
      const nameA = `${a.profile.firstName} ${a.profile.lastName}`.toLowerCase();
      const nameB = `${b.profile.firstName} ${b.profile.lastName}`.toLowerCase();
      
      if (sortOrder === 'asc') {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });
    
    setSortedUsers(filteredUsers);
  }, [users, sortOrder, searchQuery]);

  const toggleSortOrder = () => {
    setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await dispatch(deleteUser(userId)).unwrap();
        // Refresh the user list after deletion
        dispatch(fetchUsers({ page, limit: 10 }));
      } catch (error) {
        console.error('Failed to delete user:', error);
      }
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="flex justify-between mb-4 items-center">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-medium">Team Members ({sortedUsers.length})</h3>
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setView('list')}
              className={`px-3 py-1.5 text-sm ${
                view === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              List
            </button>
            <button
              type="button"
              onClick={() => setView('hierarchy')}
              className={`px-3 py-1.5 text-sm border-l border-gray-200 ${
                view === 'hierarchy'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Org Chart
            </button>
          </div>
        </div>
        {hasEditPermission && view === 'list' && (
          <button
            onClick={() => setIsAddUserOpen(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Add Team Member
          </button>
        )}
      </div>

      {view === 'hierarchy' ? (
        <OrgHierarchyView isMaximized={isMaximized} />
      ) : (
        <>
      {/* Search input */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {sortedUsers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {loading ? 'Loading users...' : 'No users found'}
        </div>
      ) : (
        <div className="border rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={toggleSortOrder}
                    >
                      <div className="flex items-center">
                        Name
                        <span className="ml-1">
                          {sortOrder === 'asc' ? '▲' : '▼'}
                        </span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedUsers.map((user) => (
                    <tr key={user._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.profile.firstName} {user.profile.lastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.profile.role}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {hasEditPermission ? (
                          <div className="flex space-x-4">
                            <button
                              onClick={() => setEditingUser(user)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Edit
                            </button>
                            {isSuperAdmin && (
                              <>
                                <button
                                  onClick={() => setPasswordChangeUser(user)}
                                  className="text-yellow-600 hover:text-yellow-900"
                                >
                                  Change Password
                                </button>
                                <button
                                  onClick={() => handleDelete(user._id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center mt-4">
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
              Page {page} of {pagination.pages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === pagination.pages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </nav>
        </div>
      )}
        </>
      )}

      {/* Add EditUserDialog */}
      <EditUserDialog
        user={editingUser}
        isOpen={!!editingUser}
        onClose={() => setEditingUser(null)}
        onSuccess={() => {
          dispatch(fetchUsers({ page, limit: 10 }));
        }}
      />

      {/* Add ChangePasswordDialog */}
      <ChangePasswordDialog
        user={passwordChangeUser}
        isOpen={!!passwordChangeUser}
        onClose={() => setPasswordChangeUser(null)}
      />

      <AddUserDialog
        isOpen={isAddUserOpen}
        onClose={() => setIsAddUserOpen(false)}
        onSuccess={() => {
          dispatch(fetchUsers({ page, limit: 10 }));
        }}
      />
    </div>
  );
};

export default UsersTab; 